import React, { useCallback, useEffect, useRef, useState } from "react";
import icon_camera from "./assets/camera.svg";
import icon_plus from "./assets/plus.svg";
import icon_minus from "./assets/minus.svg";
import icon_autoRotate from "./assets/autoRotate.svg";
import icon_arrows_in from "./assets/arrowsIn.svg";
import icon_back from "./assets/arrowCounterClockwise.svg";
import { setCurrentProductCode } from "./config"; // Import the function from config.js

function Player({ productName, selectedProductCode }) {
  const emersyaIframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerActive, setViewerActive] = useState(false);
  const [isLoadingNew, setIsLoadingNew] = useState(true);
  const scriptRef = useRef(null);
  const containerRef = useRef(null);
  const emersyaContextLost = useRef(false);

  // Complete cleanup of the Emersya viewer with improved resource handling
  const cleanupViewer = useCallback(() => {
    console.log("Cleaning up Emersya viewer...");
    emersyaContextLost.current = false;

    return new Promise((resolve) => {
      // Cancel any pending animation frames
      if (window.emViewerAnimationFrame) {
        window.cancelAnimationFrame(window.emViewerAnimationFrame);
        window.emViewerAnimationFrame = null;
      }

      // Remove global viewer if it exists
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        try {
          const emersyaViewer = window.emViewers["emersyaIframe"];

          // Pause any rendering activity if possible
          try {
            emersyaViewer.pause && emersyaViewer.pause();
          } catch (e) {
            console.log("Pause not available or error:", e);
          }

          // Explicitly release textures and materials if method exists
          try {
            emersyaViewer.releaseResources && emersyaViewer.releaseResources();
          } catch (e) {
            console.log("Resource release not available or error:", e);
          }

          // Stop all ongoing processes
          try {
            emersyaViewer.stopAllSounds();
          } catch (e) {}
          try {
            emersyaViewer.stopZoomIn();
          } catch (e) {}
          try {
            emersyaViewer.stopZoomOut();
          } catch (e) {}
          try {
            emersyaViewer.stop();
          } catch (e) {}

          // Remove all event listeners
          try {
            emersyaViewer.removeEventListener("onStateChange");
            emersyaViewer.removeEventListener(
              "onConfigurableMaterialHighlight"
            );
            emersyaViewer.removeEventListener("onSuccess");
            emersyaViewer.removeEventListener("onError");
          } catch (e) {
            console.error("Error removing event listeners:", e);
          }

          // Destroy the viewer if method exists
          try {
            if (emersyaViewer.destroyViewer) {
              emersyaViewer
                .destroyViewer()
                .then(() => console.log("Viewer destroyed successfully"))
                .catch((e) => console.error("Error destroying viewer:", e));
            }
          } catch (e) {
            console.error("Error destroying viewer:", e);
          }
        } catch (e) {
          console.error("Error during viewer cleanup:", e);
        }

        // Clear the viewer reference
        delete window.emViewers["emersyaIframe"];
      }

      // Clean up scripts
      const scripts = document.querySelectorAll('script[src*="emersya"]');
      scripts.forEach((script) => {
        try {
          document.head.removeChild(script);
        } catch (e) {
          console.error("Error removing script:", e);
        }
      });

      // Reset scriptRef
      scriptRef.current = null;

      // Clean up the container with more thorough approach
      const container = document.getElementById("emersyaIframe");
      if (container) {
        // Clear the container content
        container.innerHTML = "";

        // Force WebGL context loss by replacing the element entirely
        const parent = container.parentNode;
        if (parent) {
          // Remove old container completely
          parent.removeChild(container);

          // Create brand new container
          const newContainer = document.createElement("div");
          newContainer.id = "emersyaIframe";
          newContainer.className = container.className || "";
          newContainer.style.cssText = container.style.cssText || "";
          parent.appendChild(newContainer);
        }
      }

      // Force garbage collection if available
      if (window.gc) window.gc();

      // Add a delay to ensure cleanup completes before resolving
      setTimeout(resolve, 500);
    });
  }, []);

  // Initialize the Emersya viewer with improved error handling
  const initializeViewer = useCallback((productCode) => {
    console.log("Initializing Emersya viewer for product:", productCode);
    setIsLoadingNew(true);

    // Update the current product code in config.js
    setCurrentProductCode(productCode);

    return new Promise((resolve, reject) => {
      try {
        // Ensure container exists
        const container = document.getElementById("emersyaIframe");
        if (!container) {
          console.error("Container element not found");
          setIsLoadingNew(false);
          reject(new Error("Container element not found"));
          return;
        }

        // Create a new script element for Emersya
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = "https://cdn.emersya.com/f/emersyaLoader.js";
        script.id = "emersya-loader-" + Date.now(); // Add timestamp to make it unique
        script.setAttribute("routing", productCode);
        script.setAttribute("containerId", "emersyaIframe");

        // Add error handling to script loading
        script.onerror = (error) => {
          console.error("Error loading Emersya script:", error);
          setIsLoadingNew(false);
          reject(error);
        };

        // Store reference to this script for future cleanup
        scriptRef.current = script;

        // Wait for the viewer to initialize
        const handleInitialization = () => {
          console.log("Emersya viewer initialized");
          setViewerActive(true);
          setIsLoadingNew(false);
          resolve();
        };

        // Add event listener for initialization
        document.addEventListener(
          "emersyaViewerInitialized",
          handleInitialization,
          { once: true }
        );

        // Add to document
        document.head.appendChild(script);

        // Set a timeout in case initialization fails
        const timeoutId = setTimeout(() => {
          console.warn(
            "Emersya viewer initialization timed out after 20 seconds"
          );
          document.removeEventListener(
            "emersyaViewerInitialized",
            handleInitialization
          );
          setIsLoadingNew(false);
          reject(new Error("Initialization timed out"));
        }, 20000);

        // Update the event listener to clear the timeout
        const wrappedHandler = () => {
          clearTimeout(timeoutId);
          handleInitialization();
        };

        document.removeEventListener(
          "emersyaViewerInitialized",
          handleInitialization
        );

        document.addEventListener("emersyaViewerInitialized", wrappedHandler, {
          once: true,
        });
      } catch (error) {
        console.error("Error in initializeViewer:", error);
        setIsLoadingNew(false);
        reject(error);
      }
    });
  }, []);

  // Effect for product changes with improved sequence handling
  useEffect(() => {
    console.log("Product code changed to:", selectedProductCode);

    // Only execute if we have a product code
    if (!selectedProductCode) return;

    // Set current product code in config.js
    // Setting it here ensures it's updated immediately when the product changes
    setCurrentProductCode(selectedProductCode);

    // We need to do cleanup first, then wait a bit before initializing the new viewer
    setViewerActive(false);
    setIsLoadingNew(true);

    // Clean up existing viewer, then initialize with proper sequence
    let cleanupDone = false;

    cleanupViewer()
      .then(() => {
        cleanupDone = true;
        // Increased delay to ensure complete cleanup
        return new Promise((resolve) => setTimeout(resolve, 1000));
      })
      .then(() => {
        return initializeViewer(selectedProductCode);
      })
      .catch((error) => {
        console.error("Error during product change sequence:", error);

        // If cleanup failed but initialization didn't start, try initializing anyway
        if (!cleanupDone) {
          console.warn("Cleanup failed, attempting initialization anyway");
          return initializeViewer(selectedProductCode);
        }

        setIsLoadingNew(false);
      });

    // Cleanup function
    return () => {
      // Nothing special needed here since cleanup happens in the effect body
    };
  }, [selectedProductCode, cleanupViewer, initializeViewer]);

  // Add WebGL context loss handling
  useEffect(() => {
    const handleContextLoss = (e) => {
      console.log("WebGL context lost, preventing default behavior");
      e.preventDefault(); // Allow for context restoration
      emersyaContextLost.current = true;
      setIsLoadingNew(true);
    };

    const handleContextRestored = () => {
      console.log("WebGL context restored");
      emersyaContextLost.current = false;

      // If context was lost during normal operation (not during cleanup)
      if (viewerActive) {
        // Reinitialize the viewer with the current product
        initializeViewer(selectedProductCode).catch((error) => {
          console.error("Error reinitializing after context restore:", error);
        });
      }
    };

    // Find all canvas elements within the container and attach listeners
    const attachCanvasListeners = () => {
      const container = document.getElementById("emersyaIframe");
      if (!container) return;

      const canvases = container.querySelectorAll("canvas");
      canvases.forEach((canvas) => {
        canvas.addEventListener("webglcontextlost", handleContextLoss);
        canvas.addEventListener("webglcontextrestored", handleContextRestored);
      });
    };

    // Initial attachment
    attachCanvasListeners();

    // Set an interval to check for new canvases (in case they're added dynamically)
    const intervalId = setInterval(attachCanvasListeners, 2000);

    return () => {
      clearInterval(intervalId);

      const container = document.getElementById("emersyaIframe");
      if (!container) return;

      const canvases = container.querySelectorAll("canvas");
      canvases.forEach((canvas) => {
        canvas.removeEventListener("webglcontextlost", handleContextLoss);
        canvas.removeEventListener(
          "webglcontextrestored",
          handleContextRestored
        );
      });
    };
  }, [initializeViewer, selectedProductCode, viewerActive]);

  // Initialize container reference
  useEffect(() => {
    containerRef.current = document.getElementById("emersyaIframe");
  }, []);

  // Utility function to safely access the Emersya viewer
  const getViewer = useCallback(() => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      console.warn("Emersya viewer not available");
      return null;
    }

    // If context is lost, don't allow interaction
    if (emersyaContextLost.current) {
      console.warn("WebGL context is lost, viewer not available");
      return null;
    }

    return window.emViewers["emersyaIframe"];
  }, []);

  const zoomIn = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];

    // Consider adding a promise-based approach like in the Emersya docs:
    emersyaViewer.startZoomIn().then(() => {
      setTimeout(() => {
        emersyaViewer.stopZoomIn();
      }, 800);
    });
  }, [viewerActive]);

  const zoomOut = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.startZoomOut();
    setTimeout(() => {
      emersyaViewer.stopZoomOut();
    }, 600);
  }, []);

  const playRotation = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.play();
  }, []);

  // Reset Camera
  const resetCamera = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.resetCamera({});
    emersyaViewer.play();
  }, []);
  console.log(productName.name.it);
  // Screenshot Functionality
  function downloadBase64File(contentBase64, fileName) {
    const downloadLink = document.createElement("a");
    downloadLink.href = contentBase64;
    downloadLink.target = "_self";
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink); // Cleanup
  }

  const getScreenshot = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()}`;

    emersyaViewer.getCamera().then((cameraData) => {
      emersyaViewer
        .getScreenshot({
          width: 1024,
          height: 1024,
          transparentBackground: false,
          camera: cameraData,
        })
        .then((imageData) => {
          downloadBase64File(
            imageData,
            `${formattedDate}-${productName.name.it}.png`
          );
        });
    });
  }, [productName]);

  return (
    <div className="ay-col-span-4 md:ay-col-span-3 md:ay-sticky ay-top-0 ay-h-fit ay-relative">
      {isLoadingNew && (
        <div className="ay-absolute ay-inset-0 ay-bg-white ay-bg-opacity-75 ay-flex ay-items-center ay-justify-center ay-z-20">
          <div className="ay-animate-spin ay-rounded-full ay-h-16 ay-w-16 ay-border-t-4 ay-border-b-4 ay-border-black"></div>
        </div>
      )}

      <div className="ay-z-10 ay-absolute ay-left-10 ay-bottom-10 ay-text-md ay-flex ay-flex-col ay-gap-1">
        <img
          onClick={playRotation}
          className={`hover:ay-cursor-pointer ${
            isLoadingNew ? "ay-opacity-50" : ""
          }`}
          src={icon_autoRotate}
          alt="Auto Rotate"
        />
        <img
          onClick={zoomIn}
          className={`hover:ay-cursor-pointer ${
            isLoadingNew ? "ay-opacity-50" : ""
          }`}
          src={icon_plus}
          alt="Zoom In"
        />
        <img
          onClick={zoomOut}
          className={`hover:ay-cursor-pointer ${
            isLoadingNew ? "ay-opacity-50" : ""
          }`}
          src={icon_minus}
          alt="Zoom Out"
        />
        <img
          onClick={resetCamera}
          className={`hover:ay-cursor-pointer ${
            isLoadingNew ? "ay-opacity-50" : ""
          }`}
          src={icon_back}
          alt="Reset Camera"
        />
        <img
          onClick={getScreenshot}
          className={`hover:ay-cursor-pointer ay-w-8 ${
            isLoadingNew ? "ay-opacity-50" : ""
          }`}
          src={icon_camera}
          alt="Screenshot"
        />
      </div>

      <div
        className="ay-h-[60vh] md:ay-h-screen ay-w-full ay-relative"
        id="emersyaIframe"
      ></div>
    </div>
  );
}

export default Player;
